import { customerEntityDisplayNames } from '@src/containers/Dealer/Monitoring/types';
import { Button, ButtonGroup, Dialog, HStack, Icon, IconName, Input, Text, VStack, useDisclosure } from '@talos/kyoko';
import { entries, groupBy } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { MonitoringIconLookup } from './constants';
import { entityDisplayNames, type EntityName } from './types';

interface NewTabEntitySelectorProps<TEntity extends EntityName> {
  setSelectedEntity: (entityName?: TEntity) => void;
  isOpen: boolean;
  source?: 'monitoring' | 'dealer-monitoring';
  allowedEntities?: readonly TEntity[];
}

export function NewTabEntitySelector<TEntity extends EntityName = EntityName>({
  setSelectedEntity,
  isOpen,
  source = 'dealer-monitoring',
  allowedEntities,
}: NewTabEntitySelectorProps<TEntity>) {
  const modal = useDisclosure();
  const closeTab = useCallback(() => setSelectedEntity(), [setSelectedEntity]);
  const [searchPhrase, setSearchPhrase] = useState('');
  const displayNames = source === 'dealer-monitoring' ? customerEntityDisplayNames : entityDisplayNames;

  const displayItems = useMemo(() => {
    const allowedEntitiesSet = allowedEntities ? new Set(allowedEntities) : undefined;

    const matchingItems = entries(displayNames)
      .filter(([key]) => !allowedEntitiesSet || allowedEntitiesSet.has(key as TEntity))
      .filter(([_key, value]) => value.name.toLowerCase().includes(searchPhrase.toLowerCase()))
      .map(([key]) => key);

    const groupsWithItems = entries(groupBy(matchingItems as TEntity[], key => displayNames[key].group ?? ''));
    return groupsWithItems;
  }, [allowedEntities, displayNames, searchPhrase]);
  return (
    <Dialog
      {...modal}
      usePortal={false}
      title="Entity Selection"
      showCancel={false}
      showConfirm={false}
      width={434}
      showClose={true}
      isOpen={isOpen}
      close={closeTab}
    >
      <VStack w="100%" gap="spacing16">
        <Input
          type="text"
          placeholder="Search entity..."
          prefix={<Icon icon={IconName.Search} />}
          onChange={e => setSearchPhrase(e.target.value)}
        />
        {displayItems.map(([group, items]) => (
          <VStack w="100%" gap="spacing4" alignItems="flex-start" key={group || 'ungrouped'}>
            {group !== '' ? (
              <Text color="colors.gray.090" transform="uppercase" size="fontSizeSm">
                <HStack gap="spacing8">
                  <Icon icon={MonitoringIconLookup[group as keyof typeof MonitoringIconLookup]} />
                  {group}
                </HStack>
              </Text>
            ) : null}
            <ButtonGroup orientation="vertical" w="100%" alignItems="flex-start">
              {items.map(entityName => (
                <EntitySelectButton key={entityName} onClick={() => setSelectedEntity(entityName)}>
                  {displayNames[entityName].name}
                </EntitySelectButton>
              ))}
            </ButtonGroup>
          </VStack>
        ))}
      </VStack>
    </Dialog>
  );
}

const EntitySelectButton = styled(Button).attrs(() => ({ endIcon: IconName.ArrowRightCircle, dim: true }))`
  align-items: flex-start;
  justify-content: space-between;
  text-align: left;
  width: 100%;
`;
